import { isFunction } from '@agilepoint/utils-common';
import * as React from 'react';
import styled from 'styled-components';
import { IFileInfo, IFileUploadProps } from '../file-upload.types';
import { ActionButton } from '../../button';
import { FlexBox } from '../../flex-box';
import { StackPanel } from '../../stack-panel';
import { IBaseComponentProps } from '../../types';
import { Typography } from '../../typography';
import { selectThemeSemantics } from '../../../styling';

const Tile = styled(FlexBox).attrs({
    justifyContent: 'center',
    alignItems: 'center'
})`
    height: 40px;
    width: 40px;
    background-color: ${({ theme: { fileUpload: { colors: { tile } } } }) => tile.backgroundColor};
    color: ${({ theme: { fileUpload: { colors: { tile } } } }) => tile.foregroundColor};
`;

const DeleteButton = styled(ActionButton).attrs({
    iconName: 'ap-trash.outline'
})`
    :hover:enabled {
        .ap-icon {
            fill: ${selectThemeSemantics(({ palette }) => palette.red)};
        }
    }
`;

const renderDefaultTile = (extension?: string) => (extension ? (
    <Tile><Typography.XSmall>{extension}</Typography.XSmall></Tile>
) : null);

const Label = styled(Typography.Small)`
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ theme: { fileUpload: { colors: { item } } } }) => item.foregroundColor};
`;

const ItemWrapper = styled(FlexBox).attrs({
    className: String('ap-fileUpload-itemWrapper')
})`
    background-color: ${({ theme: { fileUpload: { colors: { item } } } }) => item.backgroundColor};
`;

interface IFilePreviewProps extends IBaseComponentProps<HTMLDivElement>, IFileInfo, Pick<IFileUploadProps, 'onRenderItemLabel' | 'onRenderItemTile' | 'onRenderItemActions'> {
    onRemove(id: string): void;
}

export const FilePreview = React.memo((props: IFilePreviewProps) => {
    const {
        name, elementRef, className, content, error, id, size, type,
        onRemove, onRenderItemActions, onRenderItemLabel, onRenderItemTile
    } = props;

    const _remove = React.useCallback(() => {
        onRemove(id);
    }, [id, onRemove]);

    const _onRemoveClick = React.useCallback((ev: React.MouseEvent) => {
        ev.stopPropagation();
        _remove();
    }, [_remove]);

    const extension = React.useMemo(() => name.split('.').pop()?.toUpperCase(), [name]);

    return (
        <ItemWrapper width="100%" className={className} ref={elementRef} justifyContent="space-between" alignItems="center">
            <StackPanel className="ap-fileUpload-itemInfo" gap="10" alignment="center">
                <>
                    {
                        isFunction(onRenderItemTile)
                            ? onRenderItemTile({ name, content, error, id, size, type })
                            : renderDefaultTile(extension)
                    }
                    {
                        isFunction(onRenderItemLabel)
                            ? onRenderItemLabel({ name, content, error, id, size, type })
                            : <Label title={name}>{name}</Label>
                    }
                </>
            </StackPanel>
            {
                isFunction(onRenderItemActions)
                    ? onRenderItemActions({ name, content, error, id, size, type }, { remove: _remove })
                    : <DeleteButton onClick={_onRemoveClick} />
            }
        </ItemWrapper>
    );
});

FilePreview.displayName = 'FilePreview';
